import React, { useEffect, useMemo, useState } from 'react';
import { TicketArticleUiModel } from '../../../../shared/ui-models/ticket.ui-model';
import { formatBytes } from '../../../../shared/services/helpers/ticket-rules.helper';
import { CHIP } from '../../../shared/ticket-visuals';
import { ago, latestId, preview, threadRows } from '../../../shared/ticket-thread.util';

interface Props {
  articles: TicketArticleUiModel[];
  onDownload: (article: TicketArticleUiModel, attachmentId: number, name: string) => void;
}

const KIND: Record<TicketArticleUiModel['direction'], { label: string; chip: string }> = {
  in: { label: 'Email from guest', chip: 'bg-blue text-neutral-500' },
  out: { label: 'Reply sent to guest', chip: 'bg-primary-100 text-primary-800' },
  note: { label: 'Internal note', chip: 'bg-creams-500 text-creams-900' }
};

const AVATAR: Record<TicketArticleUiModel['direction'], string> = {
  in: 'bg-blue text-neutral-500',
  out: 'bg-primary-500 text-white',
  note: 'bg-creams-600 text-creams-900'
};

const Avatar: React.FC<{ article: TicketArticleUiModel }> = ({ article }) => (
  <span
    aria-hidden="true"
    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-gilroy-bold ${
      AVATAR[article.direction]
    }`}
  >
    {(article.author || '?').trim().charAt(0).toUpperCase()}
  </span>
);

const When: React.FC<{ date: Date }> = ({ date }) => {
  const rel = ago(date);
  return (
    <span className="ml-auto shrink-0 whitespace-nowrap text-xs text-neutral-300">
      {date.toLocaleString()}
      {rel ? ` (${rel})` : ''}
    </span>
  );
};

/**
 * The ticket's messages as an email thread (CX, 2026-10-19): the latest message open, every
 * earlier one a single line (who, what kind, a preview, when) that opens on click, and a long
 * middle folded into "N older messages". Handoffs stay small events between messages.
 */
export const TicketThread: React.FC<Props> = ({ articles, onDownload }) => {
  const latest = latestId(articles);
  const [open, setOpen] = useState<Set<number>>(() => new Set(latest ? [latest] : []));
  const [unfolded, setUnfolded] = useState(false);

  // A new message (a reply sent, a guest email arriving) opens itself.
  useEffect(() => {
    if (latest)
      setOpen((prev) => (prev.has(latest) ? prev : new Set(Array.from(prev).concat(latest))));
  }, [latest]);

  const rows = useMemo(() => threadRows(articles, unfolded), [articles, unfolded]);
  const messages = articles.filter((a) => !a.isHandoff);
  const allOpen = messages.length > 0 && messages.every((a) => open.has(a.id));
  const toggle = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md">
      {messages.length > 1 ? (
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2 text-xs text-neutral-400">
          <span>{messages.length} messages</span>
          <button
            type="button"
            className="font-gilroy-medium text-primary-700 hover:underline"
            onClick={() => {
              if (allOpen) {
                setOpen(new Set(latest ? [latest] : []));
                setUnfolded(false);
              } else {
                setOpen(new Set(messages.map((a) => a.id)));
                setUnfolded(true);
              }
            }}
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      ) : null}
      {rows.map((row) => {
        if (row.kind === 'fold') {
          return (
            <button
              key="fold"
              type="button"
              className="flex items-center gap-3 border-b border-gray-100 px-4 py-2 text-left text-sm text-primary-700 hover:bg-creams-100"
              onClick={() => setUnfolded(true)}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full border border-primary-200 text-xs">
                {row.count}
              </span>
              {row.count} older message{row.count === 1 ? '' : 's'}
            </button>
          );
        }
        const article = row.article;
        if (article.isHandoff) {
          return (
            <div
              key={article.id}
              className="border-b border-gray-100 bg-primary-50 px-4 py-2 text-xs text-primary-800"
            >
              <span className="whitespace-pre-wrap">{article.body}</span>
              <span className="ml-2 text-primary-700">{article.createdAt.toLocaleString()}</span>
            </div>
          );
        }
        const isOpen = open.has(article.id);
        const kind = KIND[article.direction];
        return (
          <article key={article.id} className="border-b border-gray-100 last:border-b-0">
            <button
              type="button"
              aria-expanded={isOpen}
              className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-creams-100"
              onClick={() => toggle(article.id)}
            >
              <Avatar article={article} />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-gilroy-bold text-neutral-500">{article.author}</span>
                  <span className={`${CHIP} ${kind.chip}`}>{kind.label}</span>
                  {!isOpen && article.attachments.length ? (
                    <span className="text-xs text-neutral-300">
                      📎 {article.attachments.length}
                    </span>
                  ) : null}
                  <When date={article.createdAt} />
                </span>
                {!isOpen ? (
                  <span className="truncate text-sm text-neutral-400">{preview(article.body)}</span>
                ) : null}
              </span>
            </button>
            {isOpen ? (
              <div className="pb-4 pl-16 pr-4">
                <div className="whitespace-pre-wrap break-words text-sm text-neutral-500">
                  {article.body}
                </div>
                {article.attachments.length ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {article.attachments.map((file) => (
                      <button
                        key={file.id}
                        type="button"
                        className="rounded-full bg-creams-200 px-2.5 py-0.5 text-xs text-neutral-500 hover:bg-creams-400"
                        onClick={() => onDownload(article, file.id, file.filename)}
                      >
                        📎 {file.filename} · {formatBytes(file.size)}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
};
